import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bell, Loader2, MessageSquare, Play, UserX } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Settings → Smart Alerts: controls for the Missed Clock-In sweep.
export const MissedClockInSettings = ({ token }) => {
  const { toast } = useToast();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const h = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => { axios.get(`${API}/alerts/missed-clock-ins/settings`, h).then((r) => setS(r.data)).catch(() => setS(false)); }, [token]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/alerts/missed-clock-ins/settings`, patch, h);
      setS(r.data);
    } catch (e) {
      toast({ title: 'Could not save', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setSaving(false); }
  };
  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/alerts/missed-clock-ins/run-now`, {}, h);
      toast({ title: 'Sweep complete', description: `${r.data.bell} admin alert${r.data.bell === 1 ? '' : 's'} · ${r.data.nudges} worker nudge${r.data.nudges === 1 ? '' : 's'} (${r.data.sms} text${r.data.sms === 1 ? '' : 's'}) · ${r.data.digests} digest email${r.data.digests === 1 ? '' : 's'}.` });
    } catch (e) {
      toast({ title: 'Sweep failed', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setRunning(false); }
  };

  if (s === null) return null;
  if (s === false) return <p className="text-xs text-amber-400" data-testid="missed-clockin-settings-error">Couldn't load missed clock-in settings.</p>;

  return (
    <div className="ht-card p-4 space-y-4" data-testid="missed-clockin-settings">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-white flex items-center gap-2"><UserX className="w-4 h-4 text-amber-400" /> Missed clock-in alerts</h3>
          <p className="text-xs text-slate-400 mt-1">
            Scheduled workers who haven't punched in {s.missed_clockin_grace_minutes} min after shift start show on the Company dashboard,
            ring the bell for admins, and are emailed as a morning digest.
          </p>
        </div>
        <Switch checked={!!s.missed_clockin_enabled} disabled={saving} onCheckedChange={(v) => save({ missed_clockin_enabled: v })} data-testid="missed-clockin-enabled" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Grace period (minutes)</Label>
          <Input type="number" min={0} max={240} step={5} defaultValue={s.missed_clockin_grace_minutes} disabled={!s.missed_clockin_enabled || saving}
            onBlur={(e) => { const v = parseInt(e.target.value, 10); if (!Number.isNaN(v) && v !== s.missed_clockin_grace_minutes) save({ missed_clockin_grace_minutes: v }); }}
            data-testid="missed-clockin-grace" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Digest email hour (company time)</Label>
          <Input type="number" min={0} max={23} defaultValue={s.missed_clockin_digest_hour} disabled={!s.missed_clockin_enabled || saving}
            onBlur={(e) => { const v = parseInt(e.target.value, 10); if (!Number.isNaN(v) && v !== s.missed_clockin_digest_hour) save({ missed_clockin_digest_hour: v }); }}
            data-testid="missed-clockin-digest-hour" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs flex items-center gap-1"><Bell className="w-3 h-3" /> Daily digest email</Label>
          <div className="h-10 flex items-center">
            <Switch checked={!!s.missed_clockin_digest_email} disabled={!s.missed_clockin_enabled || saving} onCheckedChange={(v) => save({ missed_clockin_digest_email: v })} data-testid="missed-clockin-digest-email" />
          </div>
        </div>
      </div>
      <div className="rounded-lg border border-[#1F2D3F] p-3 space-y-3" data-testid="missed-clockin-nudge">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm text-white flex items-center gap-2"><MessageSquare className="w-4 h-4 text-sky-400" /> Nudge the worker first</p>
            <p className="text-xs text-slate-400 mt-0.5">Tell the late worker directly (in-app bell, plus a text if they have a phone on file) so they can clock in before anyone has to chase them. Skipped when an absence is already tagged.</p>
          </div>
          <Switch checked={!!s.missed_clockin_nudge_enabled} disabled={!s.missed_clockin_enabled || saving} onCheckedChange={(v) => save({ missed_clockin_nudge_enabled: v })} data-testid="missed-clockin-nudge-enabled" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Nudge after (minutes past shift start)</Label>
            <Input type="number" min={0} max={240} step={5} defaultValue={s.missed_clockin_nudge_minutes} disabled={!s.missed_clockin_enabled || !s.missed_clockin_nudge_enabled || saving}
              onBlur={(e) => { const v = parseInt(e.target.value, 10); if (!Number.isNaN(v) && v !== s.missed_clockin_nudge_minutes) save({ missed_clockin_nudge_minutes: v }); }}
              data-testid="missed-clockin-nudge-minutes" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Also send a text message (SMS)</Label>
            <div className="h-10 flex items-center">
              <Switch checked={!!s.missed_clockin_nudge_sms} disabled={!s.missed_clockin_enabled || !s.missed_clockin_nudge_enabled || saving} onCheckedChange={(v) => save({ missed_clockin_nudge_sms: v })} data-testid="missed-clockin-nudge-sms" />
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={runNow} disabled={running || !s.missed_clockin_enabled} data-testid="missed-clockin-run-now">
          {running ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />} Check now
        </Button>
        {saving && <span className="text-xs text-slate-400">Saving…</span>}
      </div>
    </div>
  );
};
